import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./fishbone.css";
import d3 from "d3";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { RCCHookToast } from "react-common-components";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import fishbone from "./fishbone";
import { IFishBoneCharComponent, IRefFishBoneChart, IRefFishBoneModals } from "./interface";
import { IIntba005 } from "../interface";
import FishBoneModals from "./modals";

const FishBoneChart: React.ForwardRefRenderFunction<IRefFishBoneChart,IFishBoneCharComponent> = ({
	parentContainerRef,
	fcaId,
	fishBoneDefaultData,
	disabled,
	updateFunction,
	canDelete
},ref) => {

	const { translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const [data, setData] = useState<Array<IIntba005>>(fishBoneDefaultData||[]);
	const [treeData,setTreeData] = useState<any>();
	const containerRef = useRef<HTMLDivElement>(null);
	const modalsRef = useRef<IRefFishBoneModals>(null);
	const svg = useRef<any>(null);
	const ishikawa = useRef<any>(null);

	const size = () => (
		{ 
			width: containerRef?.current?.offsetWidth || parentContainerRef?.current?.offsetWidth||1000, 
			height: containerRef?.current?.offsetHeight ||800 
		}
	);

	const getChildrensFromData = (parentId:string,level:number,category:number):Array<any> => {
		const actualLevel = level +1;
		const nodes = data.filter((node) => parentId !== "0" ? node.parentId === parentId : !node.parentId && node.ina005Categoria === category);

		const childrens = nodes.map((element) => ({
			nodeId :element.id,
			level:actualLevel,
			name:element.ina005Causa,
			category:element.ina005Categoria,
			children:getChildrensFromData(element.id,actualLevel,element.ina005Categoria)
		}));

		return childrens||[];
	};

	const buildTreeData = () => {

		const categories = [
			translate("Máquina"),
			translate("Mão Obra"),
			translate("Material"),
			translate("Meio Ambiente"),
			translate("Método"),
			translate("Medidas")
		];

		const tree = {
			nodeId:v4(),
			level:0,
			children: [] as any
		};

		const level = 1;
		categories.forEach((category,idx) => {
			tree.children.push({
				nodeId :"0",
				level,
				name :category,
				category :idx+1,
				children: getChildrensFromData("0",level,idx+1)
			});
		});

		setTreeData(tree);
	};

	const addNode = (parentNode:any) => {
		modalsRef.current?.actionButtonAdd(
			(newName:string) => {
				setData((prevState) => 
					[
						...prevState,
						{
							id:v4(),
							parentId:parentNode.nodeId === "0" ? "": parentNode.nodeId,
							ina005Id:0,
							ina005Ina005Id:null,
							ina005Causa:newName,
							ina005Categoria:parentNode.category,
							childrens:[]
						}
					]
				);
			}
		);
	};

	const editNode = (node:any) => {
		modalsRef.current?.actionButtonChange(
			node.name, 
			(newName:string) => {
				setData((prevState) => prevState.map((x) => {
					if(x.id === node.nodeId )
						return {...x,ina005Causa:newName};
					return x;
				}));
			}
		);
	};

	const deleteNode = (node:any) => {
		confirm(
			translate("Exclusão da causa"),
			`${translate("Deseja realmente excluir a causa")}: ${node.name} ?`,
			() => {
				setData((prevState) => prevState.filter((x) => x.id !== node.nodeId && x.parentId !== node.nodeId));
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir causa"), addToast);
			},
			true
		);
	};

	const initFishBone = () => {
		if(!treeData) return;

		const tree = {...treeData};

		ishikawa.current = fishbone(
			fcaId,
			size,
			!disabled,
			canDelete,
			{onAdd:addNode,onEdit:editNode,onDelete:deleteNode}
		);

		svg.current = d3
			.select(`#fishbone${fcaId}`)
			.append("svg")
			.attr(size())
			.datum(tree)
			.call(ishikawa.current.defaultArrow)
			.call(ishikawa.current);
		
		ishikawa.current.force().start();

		d3.select(window).on("resize", () => {
			ishikawa.current.force().size([size().width, size().height]).resume();
			svg.current.attr(size());
		});
	};

	useEffect(() => {
		buildTreeData();
	}, [data]);

	useEffectSkipInitialRender(() => {
		updateFunction();
	}, [data]);

	useEffect(() => {
		if( svg.current || ishikawa.current){
			ishikawa.current.nodes([]);
			ishikawa.current.links([]);
			svg.current.remove();
		}
		initFishBone();
	}, [treeData]);

	useEffect(() => {
		setData(fishBoneDefaultData||[]);
	}, [fishBoneDefaultData]);

	useImperativeHandle(ref, () => ({
		getValues:() => data
	}));

	return (
		<div ref={containerRef}>
		  <div id={`fishbone${fcaId}`} className="container-ishikawa"></div>
		  <div id={`fishbone${fcaId}-tooltip-container`}></div>
		  <FishBoneModals ref={modalsRef}/>
		</div>
	);
};

export default forwardRef(FishBoneChart);
